import type {
  ICartDishItem,
  TPortion,
  TSupplement,
  ISelectedSupplement,
} from "@/types";
import { safeNumber } from "@/pages/menuItemsPage/helpers";
import { pickLocalizedText } from "@/utils/languageVersions/pickLocalizedText";

export interface IMenuItemForCart {
  id: number;
  name: string;
  nameOrigin?: string | null;
  portions: TPortion[];
  supplements: TSupplement[];
}

export interface ICartDishAddonLineVm {
  text: string;
}

export interface ICartDishAddonGroupVm {
  groupId: number;
  groupName: string;
  lines: ICartDishAddonLineVm[];
}

export interface ICartDishCardVm {
  dishId: number;
  title: string;
  portionSuffix: string | null;

  addonGroups: ICartDishAddonGroupVm[];

  quantity: number;
  totalPrice: number;
  totalText: string;
  warnings: string[];
  isUnavailable: boolean;
}

const buildAddonGroups = (
  selected: ISelectedSupplement[],
  supplementsFromApi: TSupplement[]
): { groups: ICartDishAddonGroupVm[]; sum: number; hadRemoved: boolean } => {
  const groupById = new Map(supplementsFromApi.map((g) => [g.id, g]));
  const initialState: {
    groups: ICartDishAddonGroupVm[];
    sum: number;
    hadRemoved: boolean;
  } = {
    groups: [],
    sum: 0,
    hadRemoved: false,
  };

  return (selected ?? []).reduce((state, selectedGroup) => {
    const actualGroup = groupById.get(selectedGroup.supplementId);
    if (!actualGroup) {
      return {
        ...state,
        hadRemoved: true,
      };
    }

    const optionById = new Map(
      (actualGroup.options ?? []).map((option) => [option.id, option])
    );

    const optionsToRender = (selectedGroup.options ?? []).filter(
      (option) => Number(option.quantity ?? 0) > 0
    );

    const optionsVm = optionsToRender
      .map((selectedOption) => {
        const actualOption = optionById.get(selectedOption.optionId);
        if (!actualOption) return null;

        const optionName = pickLocalizedText(
          actualOption.name,
          actualOption.nameOrigin
        );
        if (!optionName) return null;

        const quantity = Number(selectedOption.quantity ?? 0);
        const price = safeNumber(actualOption.price);

        return {
          line: {
            text: `+ ${quantity > 1 ? `${quantity}x ` : ""}${optionName}`,
          },
          linePrice: price * quantity,
        };
      })
      .filter(
        (optionVm): optionVm is NonNullable<typeof optionVm> => optionVm != null
      );

    const groupName = pickLocalizedText(
      actualGroup.groupName,
      actualGroup.groupNameOrigin
    );

    const hadRemovedInGroup =
      optionsVm.length !== optionsToRender.length || !groupName;

    if (!groupName || optionsVm.length === 0) {
      return {
        ...state,
        hadRemoved: state.hadRemoved || hadRemovedInGroup,
      };
    }

    const lines = optionsVm.map((optionVm) => optionVm.line);
    const groupSum = optionsVm.reduce(
      (total, optionVm) => total + optionVm.linePrice,
      0
    );

    return {
      groups: [
        ...state.groups,
        {
          groupId: actualGroup.id,
          groupName,
          lines,
        },
      ],
      sum: state.sum + groupSum,
      hadRemoved: state.hadRemoved || hadRemovedInGroup,
    };
  }, initialState);
};

export const mapCartDishToCardVm = (
  cartItem: ICartDishItem,
  menuItem: IMenuItemForCart | null,
  currency: string
): ICartDishCardVm => {
  if (!menuItem) {
    return {
      dishId: cartItem.dishId,
      title: "",
      portionSuffix: null,
      addonGroups: [],
      quantity: cartItem.quantity,
      totalPrice: 0,
      totalText: `0 ${currency}`,
      warnings: ["Dish is unavailable"],
      isUnavailable: true,
    };
  }

  const warnings: string[] = [];

  const portions = menuItem.portions ?? [];
  const sortedPortions = [...portions].sort(
    (a, b) => safeNumber(a.price) - safeNumber(b.price)
  );

  const fallbackPortion = sortedPortions[0] ?? null;
  const selectedPortion =
    portions.find((p) => p.id === cartItem.portionId) ?? fallbackPortion;

  if (!selectedPortion || selectedPortion.id !== cartItem.portionId) {
    warnings.push("Portion was changed");
  }

  const portionPrice = safeNumber(selectedPortion?.price);
  const portionName = pickLocalizedText(
    selectedPortion?.name,
    selectedPortion?.nameOrigin
  );
  const portionSuffix = portionName ? `(${portionName})` : null;

  const {
    groups: addonGroups,
    sum: addonsSum,
    hadRemoved,
  } = buildAddonGroups(cartItem.supplements, menuItem.supplements ?? []);

  if (hadRemoved) warnings.push("Some addons were removed");

  const unitPrice = portionPrice + addonsSum;
  const totalPrice = unitPrice * Math.max(1, Number(cartItem.quantity || 1));

  return {
    dishId: cartItem.dishId,
    title: pickLocalizedText(menuItem.name, menuItem.nameOrigin) ?? "",
    portionSuffix,
    addonGroups,
    quantity: Math.max(1, Number(cartItem.quantity || 1)),
    totalPrice,
    totalText: `${totalPrice.toFixed(2)} ${currency}`,
    warnings,
    isUnavailable: false,
  };
};
